import * as React from 'react';
import { connect } from 'react-redux';
import type { ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import { formValueSelector, reduxForm } from 'redux-form';
import type { InjectedFormProps } from 'redux-form';

import type { W_MaterialCost_BulkUpsert_RM } from 'ab-api/web/materialCost/bulkUpsert';

import type { RootState } from 'af-reducers';

import { MATERIAL_BULK_RATE_UPSERT } from 'af-constants/reduxForms';

import { Button } from '@acceligentllc/storybook';

import CustomModal from 'af-components/CustomModal';

import * as MaterialCostActions from 'af-actions/materialCost';

import RateUpdateFM from './formModel';
import { MaterialRateBulkUpdateForm } from '../../../../../Shared/Materials/MaterialRateBulkUpdateForm';

interface OwnProps {
	materialIds: number[];
	showModal: boolean;
	onSubmit: () => void;
	close: () => void;
}

type Props = OwnProps & ConnectedProps<typeof connector> & InjectedFormProps<RateUpdateFM>;

const BulkRateUpdateModal: React.FC<Props> = (props) => {
	const {
		materialIds,
		showModal,
		shippingMarkup,
		salesMarkup,
		tax,
		timezone,
		updateShippingMarkup,
		updateSalesMarkup,
		updateTax,
		valid,
		close,
		change: _change,
		handleSubmit,
		upsert,
		onSubmit,
		initialize: _initialize,
	} = props;

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const change = React.useMemo(() => _change, []);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const initialize = React.useMemo(() => _initialize, []);

	React.useEffect(() => {
		if (showModal) {
			initialize(new RateUpdateFM());
		}
	}, [showModal, initialize]);

	const upsertMaterialCost = React.useCallback(async (form: RateUpdateFM) => {
		const rm: W_MaterialCost_BulkUpsert_RM = {
			materialIds,
			updateShippingMarkup: form.updateShippingMarkup,
			shippingMarkup: form.shippingMarkup,
			updateSalesMarkup: form.updateSalesMarkup,
			salesMarkup: form.salesMarkup,
			updateTax: form.updateTax,
			tax: form.tax,
		};
		await upsert(rm);
		onSubmit();
		close();
	}, [close, materialIds, onSubmit, upsert]);

	const formUpsert = React.useMemo(() => handleSubmit(upsertMaterialCost), [handleSubmit, upsertMaterialCost]);

	const handleFormSubmit = React.useCallback(() => {
		formUpsert();
	}, [formUpsert]);

	return (
		<CustomModal modalStyle="warning" showModal={showModal} size="xl">
			<CustomModal.Header
				closeModal={close}
				title="Edit Rates for Selected Materials?"
			/>
			<CustomModal.Body>
				<MaterialRateBulkUpdateForm
					change={change}
					levelWord="Company"
					salesMarkup={salesMarkup}
					shippingMarkup={shippingMarkup}
					tax={tax}
					timezone={timezone}
					updateSalesMarkup={updateSalesMarkup}
					updateShippingMarkup={updateShippingMarkup}
					updateTax={updateTax}
				/>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={close}
					style="secondary"
				/>
				<Button
					disabled={!valid}
					label="Update Rates"
					onClick={handleFormSubmit}
					style="primary"
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

const formSelect = formValueSelector(MATERIAL_BULK_RATE_UPSERT);

function mapStateToProps(state: RootState) {
	const { user: { companyData } } = state;

	if (!companyData) {
		throw new Error('Not logged in');
	}

	const { shippingMarkup, salesMarkup, tax, updateSalesMarkup, updateShippingMarkup, updateTax } = formSelect(
		state,
		RateUpdateFM.field('updateShippingMarkup'),
		RateUpdateFM.field('shippingMarkup'),
		RateUpdateFM.field('updateSalesMarkup'),
		RateUpdateFM.field('salesMarkup'),
		RateUpdateFM.field('updateTax'),
		RateUpdateFM.field('tax')
	) as RateUpdateFM;

	return {
		shippingMarkup,
		salesMarkup,
		tax,
		updateSalesMarkup,
		updateShippingMarkup,
		updateTax,
		timezone: companyData.timezone,
	};
}

function mapDispatchToProps() {
	return {
		upsert: MaterialCostActions.bulkUpsertMaterialCost,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const formConnector = reduxForm({
	form: MATERIAL_BULK_RATE_UPSERT,
	validate: RateUpdateFM.validate,
});

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector,
	formConnector
);

export default enhance(BulkRateUpdateModal);
